import { ArrowRight, Zap, BarChart3, Clock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const QuizTeaser = () => {
  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-background to-accent/5 border-t border-b border-border">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          {/* Content */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/30 rounded-full px-4 py-2 mb-6">
                <Zap className="w-4 h-4 text-accent" />
                <span className="text-accent font-semibold text-sm">2-Minute Assessment</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                See If You Qualify—Without the Credit Check
              </h2>
              <p className="text-lg text-muted-foreground">
                Get instant rate estimates and loan terms based on your property type and deal size. 
                No personal information required to start.
              </p>
            </div>

            {/* What You'll Get */}
            <div className="space-y-4">
              {[
                { icon: BarChart3, label: 'Estimated rate range', desc: 'Based on property type & loan size' },
                { icon: Clock, label: 'Approval timeline', desc: 'Know when you can close' },
                { icon: CheckCircle, label: 'Next steps', desc: 'Personalized path to funding' },
              ].map((feature, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{feature.label}</p>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="space-y-3">
              <Link to="/quiz" className="inline-block">
                <Button size="lg" className="gap-2 bg-accent hover:bg-accent/90 text-accent-foreground text-lg py-6 px-8">
                  Get Pre-Approved in 10 Minutes
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <p className="text-sm text-muted-foreground flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-success" /> No credit check
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-success" /> Instant results
                </span>
              </p>
            </div>
          </div>

          {/* Visual - Results Preview */}
          <div className="relative h-96 rounded-2xl overflow-hidden border border-border bg-card shadow-xl">
            <div className="absolute inset-0 p-8 flex flex-col justify-center">
              <div className="space-y-6">
                {/* Mock Results */}
                <div className="text-center mb-6">
                  <p className="text-sm text-muted-foreground mb-2">Your Estimated Results</p>
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-8 h-8 text-success" />
                  </div>
                  <p className="text-2xl font-bold text-foreground">Pre-Qualified</p>
                </div>

                <div className="space-y-4">
                  {[
                    { label: "Estimated Rate", value: "7.5% - 9.2%", bgClass: "bg-accent/15", textClass: "text-accent" },
                    { label: "Max Loan Amount", value: "Up to $2.3M", bgClass: "bg-primary/15", textClass: "text-primary" },
                    { label: "Approval Timeline", value: "48 Hours", bgClass: "bg-success/15", textClass: "text-success" },
                  ].map((item, idx) => (
                    <div key={idx} className={`${item.bgClass} p-4 rounded-lg`}>
                      <p className={`text-xs font-medium ${item.textClass}`}>{item.label}</p>
                      <p className={`font-bold text-lg ${item.textClass}`}>{item.value}</p>
                    </div>
                  ))}
                </div>

                {/* Countdown Teaser */}
                <div className="text-center text-sm text-muted-foreground">
                  <p>Rate estimates valid for <span className="text-warning font-semibold">24 hours</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuizTeaser;
